﻿// const units: number[] = [1, 1000, 60000, 3600000];

function pad(value: number): string {
	return ("0" + value).slice(-2);
}

function toISOString(time: TimeOnly): string {
	const ms = time.getMilliseconds();
	const short = `${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`;
	if (ms === 0) {
		return short;
	}
	return `${short}.${("00" + ms).slice(-3)}`;
}

function timeToTicks(hours: number, minutes: number, seconds: number, milliseconds: number): number {
	if (isNaN(hours)) hours = 0;
	if (isNaN(minutes)) minutes = 0;
	if (isNaN(seconds)) seconds = 0;
	if (isNaN(milliseconds)) milliseconds = 0;

	if (hours < 0 || hours > 23) throw new Error("Invalid hours");
	if (minutes < 0 || minutes > 59) throw new Error("Invalid minutes");
	if (seconds < 0 || seconds > 59) throw new Error("Invalid seconds");
	if (milliseconds < 0 || milliseconds > 999) throw new Error("Invalid milliseconds");

	return hours * 216000000 + minutes * 60000 + seconds * 1000 + milliseconds;
}

function parseString(value: string): number {
	const [hours, minutes, maybeSeconds] = value.split(":");
	const [seconds, milliseconds] = maybeSeconds.split(".");
	return timeToTicks(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10), parseInt(milliseconds, 10));
}

export class TimeOnly {
	private static _zero: TimeOnly | null = null;
	private readonly _ticks: number;

	private constructor(ticks: number) {
		this._ticks = ticks;
	}

	/**
	 * Creates a TimeOnly instance with all components set to zero
	 */
	public static zero(): TimeOnly {
		return this._zero ??= new TimeOnly(0);
	}

	get [Symbol.toStringTag](): string {
		return `TimeOnly ${toISOString(this)}`;
	}

	/**
	 * Creates a TimeOnly instance from a string in the format "HH:MM:SS" or "HH:MM:SS.mmm"
	 */
	public static fromIsoString(value: string): TimeOnly {
		return new TimeOnly(parseString(value));
	}

	/**
	 * Creates a TimeOnly instance from hours, minutes, seconds, and milliseconds components
	 */
	public static fromComponents(hours: number, minutes: number = 0, seconds: number = 0, milliseconds: number = 0): TimeOnly {
		return new TimeOnly(timeToTicks(hours, minutes, seconds, milliseconds));
	}

	getHours(): number {
		return (this._ticks / 216000000 | 0) % 24;
	}

	getMinutes(): number {
		return (this._ticks / 60000 | 0) % 60;
	}

	getSeconds(): number {
		return (this._ticks / 1000 | 0) % 60;
	}

	getMilliseconds(): number {
		return this._ticks % 1000;
	}

	getUtcDate(): Date {
		return new Date(1980, 0, 1, this.getHours(), this.getMinutes(), this.getSeconds(), this.getMilliseconds());
	}

	equals(other: TimeOnly | null | undefined): boolean {
		if (other == null) {
			return false;
		}

		return this._ticks === other._ticks;
	}

	addHours(hours: number): TimeOnly {
		return new TimeOnly(this._ticks + hours * 216000000);
	}

	addMinutes(minutes: number): TimeOnly {
		return new TimeOnly(this._ticks + minutes * 60000);
	}

	addSeconds(seconds: number): TimeOnly {
		return new TimeOnly(this._ticks + seconds * 1000);
	}

	compareTo(other: TimeOnly | null | undefined): number {
		if (other == null) {
			return 1;
		}

		return this._ticks - other._ticks;
	}

	isBetween(start: TimeOnly, end: TimeOnly): boolean {
		const startTicks = start._ticks;
		const endTicks = end._ticks;

		return startTicks <= endTicks
			? (startTicks <= this._ticks && endTicks > this._ticks)
			: (startTicks <= this._ticks || endTicks > this._ticks);
	}

	toString(): string {
		return toISOString(this);
	}

	[Symbol.toPrimitive](hint: string): number | string {
		if (hint === "number") {
			return this._ticks;
		}

		return toISOString(this);
	}

	toJSON(): string {
		return toISOString(this);
	}
}
